import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { matchingAPI } from '../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

/**
 * Auto-matching is a budget-boxed chunked chain (CLAUDE.md section 13, same
 * engine as the admin diagnostics chains) instead of one synchronous
 * transaction x invoice cross-product per click -- this renders the "Run
 * Auto-Matching" control as a chain: run N, X/Y transactions processed,
 * live suggestion count, and a Stop control while a chain is in flight.
 * Mirrors GmailAmountReextractionPanel.jsx's ChainSection + useJobPolling
 * shape, collapsed to a single operation (no separate Preview/Apply here).
 */
export function AutoMatchChainControl({ entityId, onRunCompleted, testIdPrefix = 'auto-match-chain' }) {
  const [chain, setChain] = useState(null);
  const [progress, setProgress] = useState(null);
  const [failed, setFailed] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const fetchChain = useCallback(async () => {
    try {
      const { data } = await matchingAPI.autoMatchChainStatus(entityId);
      setChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, [entityId]);

  const startJobPolling = useCallback((jobId) => {
    stopPolling();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await matchingAPI.autoMatchJobStatus(jobId);
        if (data.status === 'completed') {
          stopPolling();
          setProgress(null);
          setFailed(null);
          fetchChain();
          const result = data.result || {};
          const created = result.matches_created || 0;
          toast.success(
            result.truncated
              ? `Auto-match run complete -- continuing chain (${created} new suggestion${created !== 1 ? 's' : ''} so far)`
              : `Auto-matching complete: ${created} new suggestion${created !== 1 ? 's' : ''}`
          );
          if (onRunCompleted) onRunCompleted(result);
        } else if (data.status === 'failed') {
          stopPolling();
          setProgress(data.progress || null);
          setFailed(data.error);
          fetchChain();
          toast.error(data.error || 'Auto-match run failed');
        } else if (data.status === 'running') {
          setProgress(data.progress || null);
        }
      } catch (err) {
        stopPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, fetchChain, onRunCompleted]);

  // Recover an in-flight/last-finished job for this scope on mount (or when
  // the scope changes), mirroring GmailAmountReextractionPanel.jsx.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await matchingAPI.autoMatchJobLatest(entityId);
        if (cancelled) return;
        const job = data?.job;
        if (job?.status === 'running') {
          setProgress(job.progress || null);
          startJobPolling(job._id);
        } else if (job?.status === 'failed') {
          setProgress(job.progress || null);
          setFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => { fetchChain(); }, [fetchChain]);

  // Keep polling chain status while running, including the gap between one
  // run finishing and its successor starting (no job is "running" to poll
  // in that window).
  useEffect(() => {
    if (chain?.status !== 'running') return undefined;
    const id = setInterval(fetchChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [chain?.status, fetchChain]);

  const runAutoMatch = async () => {
    setStarting(true);
    setFailed(null);
    try {
      const { data } = await matchingAPI.autoMatchChainStart(entityId);
      if (data.job_id) {
        startJobPolling(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An auto-match chain is already running for this scope -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const stopChain = async () => {
    if (!chain?._id) return;
    if (!window.confirm('Stop the running auto-match chain? The current run finishes its time window normally.')) return;
    setStopping(true);
    try {
      await matchingAPI.autoMatchChainStop(chain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setStopping(false);
    }
  };

  const restartChain = async () => {
    if (!window.confirm('Restart the auto-match chain from the beginning? Only safe for a stalled chain -- the backend refuses if a run is still active.')) return;
    setRestarting(true);
    try {
      const { data } = await matchingAPI.autoMatchChainRestart(entityId);
      if (data.job_id) {
        startJobPolling(data.job_id);
        toast.success('Chain restarted from the beginning.');
      } else if (data.status === 'chain_running') {
        toast.error(data.detail || 'Chain is still active -- not stalled, restart refused.');
      }
      fetchChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setRestarting(false);
    }
  };

  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const matchesCreated = live?.matches_created;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={runAutoMatch} disabled={starting || polling} data-testid={`${testIdPrefix}-run`}>
          {(starting || polling) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : 'Run Auto-Matching'}
        </Button>
        {running && (
          <Button onClick={stopChain} disabled={stopping} size="sm" variant="outline" data-testid={`${testIdPrefix}-stop`}>
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {stalled && (
          <Button onClick={restartChain} disabled={restarting} size="sm" variant="outline" data-testid={`${testIdPrefix}-restart`}>
            {restarting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Restart from beginning
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>
      {chain && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap" data-testid={`${testIdPrefix}-summary`}>
          <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
            {chain.status}
          </Badge>
          <span>
            run {fmtInt(chain.runs_count)}
            {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} transactions</>}
            {matchesCreated != null && <>, {fmtInt(matchesCreated)} suggestion{matchesCreated !== 1 ? 's' : ''}</>}
          </span>
          {stalled && (
            <span className="text-amber-600 dark:text-amber-400">
              last active {heartbeatAge}s ago -- watchdog will resume it
            </span>
          )}
          {chain.status === 'error' && chain.error && (
            <span className="text-red-600 dark:text-red-400">{chain.error}</span>
          )}
          {failed && <span className="text-red-600 dark:text-red-400">{failed}</span>}
        </div>
      )}
    </div>
  );
}
